import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { base44 } from '@/api/base44Client';
import { pagesConfig } from '@/pages.config';
import { trackPageview } from '@/lib/analytics';

function getSessionId() {
    let id = sessionStorage.getItem('visitor_session_id');
    if (!id) {
        id = Date.now().toString(36) + Math.random().toString(36).substr(2);
        sessionStorage.setItem('visitor_session_id', id);
    }
    return id;
}

export default function NavigationTracker() {
    const location = useLocation();
    const { isAuthenticated } = useAuth();
    const { Pages, mainPage } = pagesConfig;
    const mainPageKey = mainPage ?? Object.keys(Pages)[0];
    const lastPath = useRef(null);

    useEffect(() => {
        const pathname = location.pathname;
        if (pathname === lastPath.current) return;
        lastPath.current = pathname;

        let pageName;

        if (pathname === '/' || pathname === '') {
            pageName = mainPageKey || 'Home';
        } else {
            const pathSegment = pathname.replace(/^\//, '').split('/')[0];
            const pageKeys = Object.keys(Pages);
            const matchedKey = pageKeys.find(
                key => key.toLowerCase() === pathSegment.toLowerCase()
            );
            pageName = matchedKey || pathSegment;
        }

        // Log page view for analytics — deferred to avoid blocking LCP
        const logPageView = async () => {
            try {
                const sessionId = getSessionId();
                let country = 'Unknown';
                let city = 'Unknown';

                try {
                    const locRes = await base44.functions.invoke('getVisitorLocation', {});
                    if (locRes?.data) {
                        country = locRes.data.country || 'Unknown';
                        city = locRes.data.city || 'Unknown';
                    }
                } catch (e) {
                    // Location not critical
                }

                await base44.entities.PageView.create({
                    page_name: pageName,
                    page_path: pathname,
                    country,
                    city,
                    session_id: sessionId
                });
            } catch (e) {
                // Silently fail
            }

            if (isAuthenticated && pageName) {
                base44.appLogs.logUserInApp(pageName).catch(() => {});
            }
        };

        // Google Analytics 4 pageview (immediate, non-blocking)
        trackPageview(pathname);

        // Defer analytics well past LCP window so they never compete with critical path
        setTimeout(() => logPageView(), 3000);
    }, [location, isAuthenticated, Pages, mainPageKey]);

    return null;
}